import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useAuditsForInteractionId } from '@/common/hooks/audits/useAuditsForInteractionId';
import { useInteraction } from '@/common/hooks/useInteraction';
import { useInteractionParams } from '@/common/hooks/useInteractionParams';
import {
  AUDIT_LOCKED_STATUS_TOOLTIP,
  MANUAL_TRANSITION_TERMINAL_STATUSES,
  STATUS_AUTOMATION_LOCKED_STATUS,
} from '@/constants/interactions';
import { useInteractionLockedContext } from '@/views/Interactions/context/InteractionLockedContext';
import { getTransactionStatusList } from '@/views/Interactions/helpers/InteractionStatus.helper';
import { useUpdateInteractionStatus } from '@/views/Interactions/hooks/useUpdateInteractionStatus';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import {
  TrackNavigationEvent,
  useTrackNavigationAnalytics,
} from '$analytics/useTrackAnalytics/domains/navigation';
import {
  isCommunityListingStatus,
  oldestAuditAllowsManualTransition,
  togglingCommunityListingPromotionCard,
} from './useInteractionStatus.helpers';

export const useInteractionStatus = (skip = false) => {
  const { interactionId, interactionType } = useInteractionParams();
  const trackNavEvent = useTrackNavigationAnalytics();
  const router = useRouter();
  const trackCommunityEvent = useTrackCommunityAnalytics();
  const [status, setStatus] = useState('');

  const shouldSkip = !interactionType || skip;
  const { data, isLoading, mutate } = useInteraction(
    interactionId,
    interactionType,
    shouldSkip,
  );

  const updateStatus = useUpdateInteractionStatus(
    interactionType!,
    `${interactionId}`,
    data?.showOnInsideListings,
  );

  useEffect(() => {
    setStatus(data?.status ?? '');
  }, [data?.status, setStatus]);

  const statuses = getTransactionStatusList(
    interactionType,
    data?.representation ?? null,
  );

  const isStatusLockedByAutomation =
    STATUS_AUTOMATION_LOCKED_STATUS.includes(status);

  const { isLocked } = useInteractionLockedContext();

  // Expired/Lost/Rejected stay in the locked experience (the whole transaction
  // is editable only after unlocking). But the status dropdown
  // alone stays locked — even once the transaction is unlocked — when the
  // interaction has an audit that has progressed past its early state.
  const isManualTransitionCandidate =
    MANUAL_TRANSITION_TERMINAL_STATUSES.includes(status);

  const {
    data: audits,
    isLoading: auditsLoading,
    error: auditsError,
  } = useAuditsForInteractionId(
    isManualTransitionCandidate ? interactionId : null,
  );

  const auditBlocksStatusEdit = !oldestAuditAllowsManualTransition(audits);

  const isStatusEditBlockedByAudit =
    auditsLoading || auditsError !== undefined || auditBlocksStatusEdit;

  const isTransactionStatusLocked =
    isStatusLockedByAutomation || isLocked || isStatusEditBlockedByAudit;

  // The read-only status only explains itself once the transaction is unlocked;
  // while it is locked the transaction-level alert already says why.
  const resolveLockedTooltip = (statusTooltip?: string) => {
    if (isLocked) return undefined;
    if (statusTooltip) return statusTooltip;
    return auditBlocksStatusEdit ? AUDIT_LOCKED_STATUS_TOOLTIP : undefined;
  };

  const displayStatuses = isTransactionStatusLocked
    ? statuses.map((entry) =>
        entry.value === status
          ? { ...entry, tooltip: resolveLockedTooltip(entry.tooltip) }
          : entry,
      )
    : statuses;

  const onChangeStatus = async (newStatus: string) => {
    trackNavEvent(TrackNavigationEvent.SidebarStatusUpdate, {
      oldStatus: status,
      newStatus,
    });

    if (
      togglingCommunityListingPromotionCard({
        oldStatus: status,
        newStatus,
        market: data?.property?.state ?? '',
      }) &&
      router.pathname.includes('/cover-sheet')
    ) {
      trackCommunityEvent(TrackCommunityEvent.PromotionCardDisableEnable, {
        disabled: !isCommunityListingStatus({
          status: newStatus,
          market: data?.property?.state ?? '',
        }),
      });
    }

    setStatus(newStatus);
    const currentValue = status;
    const success = await updateStatus(newStatus);
    if (success) {
      void mutate();
    } else {
      setStatus(currentValue);
    }
  };

  return {
    status,
    statuses: displayStatuses,
    loading: isLoading,
    onChangeStatus,
    isArchived: data?.isArchived ?? false,
    isTransactionStatusLocked,
  };
};
